import { Controller } from "@hotwired/stimulus"
import { ensureLeaflet } from "./vendor_loader"
import { createBaseMap, satelliteLayer } from "../lib/leaflet_map"
import { boundsParams } from "../lib/bbox"
import { fetchJson } from "../lib/api"
import { logEvent } from "../lib/client_logger"
import { esc } from "../lib/html"
import { row, empty, note, label, regsHtml, fishingHtml, flowHtml } from "../lib/water_panels"
import {
  fishLayerStatus,
  waterFeaturesStatus,
  waterLayerLoadPlan
} from "../lib/water_map_load_policy"
import { responseIsCurrent } from "../lib/request_freshness"
import { MAP_VIEW_POLICY } from "../lib/map_view_policy"
import { WEB_EVENTS } from "../lib/web_events"
import { createAbortableDebounce } from "../lib/abortable_debounce"

// Leaflet is shared globally; load it from the controller so Turbo visits work.
let L

// The water map: renders stored boat ramps, fishing access, and USGS gauge
// locations (no upstream calls), and a tabbed detail modal. Live flow is fetched
// only when the user opens the Flow tab and picks a gauge.
export default class extends Controller {
  static targets = ["container", "status", "toggle", "modal", "title", "sub", "panel", "tabBtn", "fishSelect"]
  static values = { featuresUrl: String, accessUrl: String, riversUrl: String, lakesUrl: String, statesUrl: String, speciesUrl: String, fishUrl: String, usgsSiteUrl: String, initialLat: Number, initialLng: Number, initialZoom: Number, layerLimits: Object }

  connect() {
    this.connected = true
    this.loadRequestId = 0
    this.fishRequestId = 0
    this.accessRequestId = 0
    this.#boot().catch(error => this.#bootFailed(error))
  }

  async #boot() {
    await ensureLeaflet()
    if (!this.connected) return
    L = window.L

    const lat = this.hasInitialLatValue ? this.initialLatValue : MAP_VIEW_POLICY.defaultHome.lat
    const lng = this.hasInitialLngValue ? this.initialLngValue : MAP_VIEW_POLICY.defaultHome.lng
    const zoom = this.hasInitialZoomValue ? this.initialZoomValue : MAP_VIEW_POLICY.waterDefaultZoom
    this.map = createBaseMap(L, this.containerTarget, { lat, lng, zoom })
    this.satelliteLayer = satelliteLayer(L) // optional base, toggled below

    // rivers/lakes/states added first so they draw beneath the markers.
    this.groups = {
      rivers: L.layerGroup(), lakes: L.layerGroup(), states: L.layerGroup(),
      ramps: L.layerGroup(), fishing: L.layerGroup(), gauges: L.layerGroup(), fish: L.layerGroup()
    }
    Object.values(this.groups).forEach(g => g.addTo(this.map))

    this.toggleTargets.forEach(t => t.addEventListener("change", () => this.#onToggle(t)))
    if (this.#on("satellite")) this.satelliteLayer.addTo(this.map)
    this.#loadSpeciesOptions()
    this.loadCoordinator = createAbortableDebounce(signal => this.#load(signal), 300)
    this.map.on("moveend", () => this.loadCoordinator.schedule())

    this.flyHandler = e => this.map.flyTo([e.detail.lat, e.detail.lon], e.detail.zoom || 12)
    window.addEventListener(WEB_EVENTS.flyTo, this.flyHandler)
    this.escHandler = e => { if (e.key === "Escape") this.closeModal() }
    document.addEventListener("keydown", this.escHandler)

    requestAnimationFrame(() => {
      if (!this.connected || !this.map) return
      this.map.invalidateSize()
      this.loadCoordinator.runNow()
    })
  }

  #bootFailed(error) {
    if (!this.connected) return
    logEvent("water_map.initialize_failed", error)
    if (this.hasStatusTarget) this.statusTarget.textContent = "Map failed to load. Check your connection and try again."
  }

  disconnect() {
    this.connected = false
    this.loadRequestId += 1
    this.fishRequestId += 1
    this.accessRequestId += 1
    this.loadCoordinator?.stop()
    this.loadCoordinator = null
    if (this.flyHandler) window.removeEventListener(WEB_EVENTS.flyTo, this.flyHandler)
    if (this.escHandler) document.removeEventListener("keydown", this.escHandler)
    if (this.map) this.map.remove()
    this.map = null
  }

  // ── Layers ────────────────────────────────────────────────────────────────
  #load(signal = null) {
    if (!this.connected || !this.map) return
    const b = this.map.getBounds()
    const bbox = boundsParams(b)
    const requestId = ++this.loadRequestId
    this.#loadFeatures(bbox, requestId, signal)   // ramps + gauges (our DB — any zoom)
    this.#loadWater(b, bbox, requestId, signal)   // stored rivers + lakes (zoom-gated)
    this.#loadStates(bbox, requestId, signal)     // state outlines (any zoom)
    this.#loadFish(bbox, ++this.fishRequestId, signal) // waters holding the picked species
  }

  // ── Fish-type layer ─────────────────────────────────────────────────────────
  async #loadSpeciesOptions() {
    if (!this.hasFishSelectTarget || !this.hasSpeciesUrlValue) return
    const d = await this.#json(this.speciesUrlValue, "water_map.species_options_failed")
    if (!this.connected || !this.hasFishSelectTarget) return
    for (const s of d?.species || []) {
      const opt = document.createElement("option")
      opt.value = s; opt.textContent = s
      this.fishSelectTarget.appendChild(opt)
    }
  }

  onFish() { this.#loadFish(null, ++this.fishRequestId) }

  async #loadFish(bbox, requestId = ++this.fishRequestId, signal = null) {
    if (!this.hasFishSelectTarget || !this.hasFishUrlValue) return
    this.groups.fish.clearLayers()
    const species = this.fishSelectTarget.value
    if (!species) { this.#setStatus(""); return }

    const b = bbox || boundsParams(this.map.getBounds())
    const d = await this.#json(`${this.fishUrlValue}?${b}&species=${encodeURIComponent(species)}`, "water_map.fish_layer_failed", signal)
    if (!this.#fishCurrent(requestId)) return
    ;(d?.waters || []).forEach(w => {
      L.marker([w.lat, w.lon], { icon: this.#emoji("🐟") })
        .bindPopup(`<b>${esc(w.name)}</b><br>${esc((w.species || []).join(", "))}`)
        .addTo(this.groups.fish)
    })
    this.#setStatus(fishLayerStatus({ waters: d?.waters, species }))
  }

  async #loadStates(bbox, requestId, signal = null) {
    if (!this.#on("states") || !this.hasStatesUrlValue) return
    this.groups.states.clearLayers()
    const d = await this.#json(`${this.statesUrlValue}?${bbox}`, "water_map.states_layer_failed", signal)
    if (!this.#loadCurrent(requestId)) return
    if (d?.geojson) L.geoJSON(d.geojson, {
      style: { color: "#334155", weight: 2, fill: false, dashArray: "5 3" },
      interactive: false
    }).addTo(this.groups.states)
  }

  async #loadFeatures(bbox, requestId, signal = null) {
    if (!this.hasFeaturesUrlValue) return
    const data = await this.#json(`${this.featuresUrlValue}?${bbox}`, "water_map.features_layer_failed", signal)
    if (!this.#loadCurrent(requestId)) return
    if (!data) return
    this.groups.ramps.clearLayers(); this.groups.fishing.clearLayers(); this.groups.gauges.clearLayers()
    ;(data.accesses || []).forEach(a => {
      const isRamp = a.kind === "boat_ramp"
      L.marker([a.lat, a.lon], { icon: this.#emoji(isRamp ? "🚤" : "🎣") })
        .on("click", () => this.openAccess(a.id))
        .addTo(this.groups[isRamp ? "ramps" : "fishing"])
    })
    ;(data.gauges || []).forEach(g => {
      L.circleMarker([g.lat, g.lon], { radius: 5, color: "#1e88e5", weight: 2, fillColor: "#42a5f5", fillOpacity: 0.9 })
        .on("click", () => window.dispatchEvent(new CustomEvent(WEB_EVENTS.usgsSite, { detail: { site: g.site_no, title: g.river || g.name } })))
        .addTo(this.groups.gauges)
    })
    this.#setStatus(waterFeaturesStatus(data))
  }

  async #loadWater(bounds, bbox, requestId, signal = null) {
    const plan = waterLayerLoadPlan({
      bounds,
      layerLimits: this.#layerLimits(),
      enabled: { rivers: this.#on("rivers"), lakes: this.#on("lakes") }
    })

    for (const layer of plan.layers) {
      if (signal?.aborted) return
      if (!layer.enabled) continue
      this.groups[layer.key].clearLayers()
      if (!layer.fetch) continue
      const url = this.#waterLayerUrl(layer.key)
      if (!url) continue
      const d = await this.#json(`${url}?${bbox}`, `water_map.${layer.key}_layer_failed`, signal)
      if (!this.#loadCurrent(requestId)) return
      if (d?.geojson) L.geoJSON(d.geojson, this.#waterLayerOptions(layer.key)).addTo(this.groups[layer.key])
    }

    if (plan.status) this.#setStatus(plan.status)
  }

  #on(layer) {
    const t = this.toggleTargets.find(t => t.dataset.layer === layer)
    return t ? t.checked : true
  }

  #layerLimits() {
    return this.hasLayerLimitsValue ? this.layerLimitsValue : {}
  }

  #onToggle(t) {
    if (t.dataset.layer === "satellite") {
      t.checked ? this.satelliteLayer.addTo(this.map) : this.map.removeLayer(this.satelliteLayer)
      return
    }
    const g = this.groups[t.dataset.layer]
    if (t.checked) { g.addTo(this.map); this.#load() } else { this.map.removeLayer(g) }
  }

  #waterLayerUrl(key) {
    if (key === "rivers") return this.hasRiversUrlValue ? this.riversUrlValue : null
    return this.hasLakesUrlValue ? this.lakesUrlValue : null
  }

  #waterLayerOptions(key) {
    if (key === "rivers") {
      return {
        style: { color: "#2563eb", weight: 2, opacity: 0.8 },
        onEachFeature: (f, l) => l.bindPopup(`<b>${esc(f.properties?.name || "River")}</b>`)
      }
    }
    return {
      style: { color: "#2563eb", weight: 1, fillColor: "#60a5fa", fillOpacity: 0.35 },
      onEachFeature: (f, l) => l.bindPopup(`<b>${esc(f.properties?.name || "Lake")}</b>`)
    }
  }

  async #json(url, scope = "water_map.fetch_failed", signal = null) {
    try { return await fetchJson(url, signal ? { signal } : {}) } catch (error) {
      if (error?.name === "AbortError") return null
      logEvent(scope, error)
      if (this.connected && this.hasStatusTarget && !this.statusTarget.textContent) {
        this.statusTarget.textContent = "Some map data couldn't load."
      }
      return null
    }
  }

  #loadCurrent(requestId) {
    return responseIsCurrent({ connected: this.connected, requestId, latestRequestId: this.loadRequestId })
  }

  #fishCurrent(requestId) {
    return responseIsCurrent({ connected: this.connected, requestId, latestRequestId: this.fishRequestId })
  }

  #accessCurrent(requestId) {
    return responseIsCurrent({ connected: this.connected, requestId, latestRequestId: this.accessRequestId })
  }

  #emoji(ch) {
    return L.divIcon({ html: `<div style="font-size:18px;line-height:1">${ch}</div>`, className: "", iconSize: [20, 20], iconAnchor: [10, 10] })
  }

  // ── Detail modal ────────────────────────────────────────────────────────────
  async openAccess(id) {
    if (!this.hasModalTarget || !this.hasTitleTarget || !this.hasSubTarget || !this.hasAccessUrlValue) return
    const requestId = ++this.accessRequestId
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    this.titleTarget.textContent = "Loading…"
    this.subTarget.textContent = ""
    this.flowLoaded = false
    try {
      const bundle = await fetchJson(`${this.accessUrlValue}/${id}`)
      if (!this.#accessCurrent(requestId)) return
      if (!bundle?.access) throw new Error("Access details response is missing access data")
      this.bundle = bundle
      this.#renderStatic()
      this.#showTab("overview")
    } catch (error) {
      logEvent("water_map.access_details_failed", error)
      if (this.#accessCurrent(requestId)) this.titleTarget.textContent = "Couldn't load details"
    }
  }

  closeModal() {
    this.accessRequestId += 1
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
  }

  tab(e) { this.#showTab(e.currentTarget.dataset.tab) }

  #showTab(name) {
    this.tabBtnTargets.forEach(t => {
      const on = t.dataset.tab === name
      t.classList.toggle("border-blue-600", on)
      t.classList.toggle("text-gray-900", on)
      t.classList.toggle("border-transparent", !on)
    })
    this.panelTargets.forEach(p => p.classList.toggle("hidden", p.dataset.panel !== name))
  }

  #renderStatic() {
    const b = this.bundle
    const wb = b.water_body
    this.titleTarget.textContent = b.access.name
    this.subTarget.textContent = wb ? `${wb.name} · ${wb.kind}` : label(b.access.kind)

    // Overview
    const a = b.access
    const ov = [row("Type", label(a.kind))]
    if (a.surface) ov.push(row("Surface", a.surface))
    if (a.fee != null) ov.push(row("Fee", a.fee ? "Yes" : "No"))
    if (a.activities?.length) ov.push(row("Activities", a.activities.map(x => label(x)).join(", ")))
    if (a.facilities?.length) ov.push(row("Facilities", a.facilities.map(x => label(x)).join(", ")))
    let html = ov.join("")
    if (a.description) html += `<p class="mt-2 text-gray-600">${esc(a.description)}</p>`
    if (a.restrictions) html += `<div class="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">⚠ ${esc(a.restrictions)}</div>`
    if (a.web_page) html += `<a href="${esc(a.web_page)}" target="_blank" rel="noopener" class="mt-3 inline-block text-blue-600 hover:underline">Official site ↗</a>`
    const overview = this.#panel("overview")
    if (overview) overview.innerHTML = html

    // Regulations
    const regulations = this.#panel("regulations")
    if (regulations) regulations.innerHTML = regsHtml(wb)
    // Fishing
    const fishing = this.#panel("fishing")
    if (fishing) fishing.innerHTML = fishingHtml(wb)
    // Flow — list nearby gauges; live data loads on click
    const flow = this.#panel("flow")
    if (!flow) return
    flow.innerHTML = flowHtml(b.gauges)
    flow.querySelectorAll("[data-site]").forEach(btn =>
      btn.addEventListener("click", () => this.#loadFlow(btn.dataset.site, btn))
    )
  }

  async #loadFlow(site, btn) {
    if (!this.hasUsgsSiteUrlValue) return
    const requestId = this.accessRequestId
    const flow = this.#panel("flow")
    if (!flow) return
    const box = flow.querySelector(`[data-flow="${site}"]`)
    if (!box) return
    box.classList.remove("hidden")
    box.innerHTML = `<span class="text-gray-400">Loading live readings…</span>`
    try {
      const d = await (await fetch(`${this.usgsSiteUrlValue}?site=${site}&period=P7D`)).json()
      if (!this.#accessCurrent(requestId)) return
      if (!d.ok) { box.innerHTML = empty(d.error || d.message || "No live data."); return }
      const fmt = (p, unit) => p && p.latest ? `${p.latest.v} ${unit}` : "—"
      box.innerHTML =
        row("Flow", fmt(d.discharge, "cfs")) +
        row("Gage height", fmt(d.gageHeight, "ft")) +
        (d.discharge?.latest ? `<div class="mt-1 text-xs text-gray-400">As of ${new Date(d.discharge.latest.t).toLocaleString()}</div>` : "")
    } catch (error) {
      logEvent("water_map.usgs_flow_failed", error)
      if (this.#accessCurrent(requestId)) box.innerHTML = empty("Couldn't reach USGS.")
    }
  }

  #panel(name) { return this.panelTargets.find(p => p.dataset.panel === name) }

  #setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
